import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  MenuItem,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  AlertTitle,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Switch,
  FormControlLabel,
  Divider,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  Block,
  WarningAmber,
  CheckCircle,
  Undo,
  Tune,
  PlayArrow,
  Refresh,
  ChevronRight,
  FilterList,
  ContentCopy,
  Gavel,
  ReceiptLong,
} from '@mui/icons-material';
import {
  PageHeader,
  StatCard,
  FilterBar,
  FilterSpacer,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  StatusDot,
  StatusTone,
  TypePill,
  TableEmptyState,
} from '../../components/common/pageKit';
import { formatCurrency } from '../../utils/currency';
import { useAuth } from '../../context/AuthContext';
import scmService, {
  MatchException,
  MatchSummary,
  ExceptionType,
  ExceptionSeverity,
  ExceptionStatus,
  InvoiceMatchStatus,
  EXCEPTION_TYPES,
  PurchaseInvoiceOption,
  MatchEvaluateResult,
} from '../../services/scmService';

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

/** DECIMAL/VARCHAR-numeric columns arrive as strings (see scmService.ts header comment). */
const toNumOrNull = (v: number | string | null | undefined): number | null => {
  if (v === null || v === undefined) return null;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : null;
};

const fmtQty = (v: number | string | null | undefined): string => {
  const n = toNumOrNull(v);
  return n === null ? '—' : n.toLocaleString('en-IN', { maximumFractionDigits: 2 });
};

const fmtMoney = (v: number | string | null | undefined): string => {
  const n = toNumOrNull(v);
  return n === null ? '—' : formatCurrency(n);
};

const fmtPct = (v: number | string | null | undefined): string => {
  const n = toNumOrNull(v);
  return n === null ? '—' : `${n > 0 ? '+' : ''}${n.toFixed(2)}%`;
};

// ---------------------------------------------------------------------------
// Exception presentation
// ---------------------------------------------------------------------------

const severityTone: Record<ExceptionSeverity, StatusTone> = {
  BLOCKING: 'error',
  WARNING: 'warning',
};

const exceptionStatusTone: Record<ExceptionStatus, StatusTone> = {
  OPEN: 'error',
  RESOLVED: 'success',
  OVERRIDDEN: 'info',
};

const invoiceStatusTone: Record<InvoiceMatchStatus, StatusTone> = {
  UNMATCHED: 'neutral',
  MATCHED: 'success',
  WITHIN_TOLERANCE: 'warning',
  EXCEPTION: 'error',
  OVERRIDDEN: 'info',
};

const EXCEPTION_LABEL: Record<ExceptionType, string> = {
  NO_PO: 'No PO Link',
  NO_GRN: 'No GRN',
  QTY_OVER_RECEIPT: 'Qty Over-Receipt',
  QTY_UNDER_RECEIPT: 'Qty Under-Receipt',
  PRICE_VARIANCE: 'Price Variance',
  VALUE_VARIANCE: 'Value Variance',
  DUPLICATE_INVOICE: 'Duplicate Invoice',
  TAX_MISMATCH: 'Tax Mismatch',
  UOM_MISMATCH: 'UOM Mismatch',
  VENDOR_MISMATCH: 'Vendor Mismatch',
};

const EXCEPTION_TINT: Record<ExceptionType, string> = {
  NO_PO: '#64748b',
  NO_GRN: '#0284c7',
  QTY_OVER_RECEIPT: '#dc2626',
  QTY_UNDER_RECEIPT: '#d97706',
  PRICE_VARIANCE: '#dc2626',
  VALUE_VARIANCE: '#dc2626',
  DUPLICATE_INVOICE: '#991b1b',
  TAX_MISMATCH: '#7c3aed',
  UOM_MISMATCH: '#64748b',
  VENDOR_MISMATCH: '#dc2626',
};

const DIMENSION_LABEL: Record<ExceptionType, string> = {
  NO_PO: 'PO Link',
  NO_GRN: 'Goods Receipt',
  QTY_OVER_RECEIPT: 'Quantity',
  QTY_UNDER_RECEIPT: 'Quantity',
  PRICE_VARIANCE: 'Unit Price',
  VALUE_VARIANCE: 'Line Value',
  DUPLICATE_INVOICE: 'Invoice Number',
  TAX_MISMATCH: 'GST',
  UOM_MISMATCH: 'Unit of Measure',
  VENDOR_MISMATCH: 'Vendor',
};

const QTY_TYPES: ExceptionType[] = ['QTY_OVER_RECEIPT', 'QTY_UNDER_RECEIPT'];
const MONEY_TYPES: ExceptionType[] = ['PRICE_VARIANCE', 'VALUE_VARIANCE', 'TAX_MISMATCH'];

/**
 * The PO / GRN / Invoice comparison cells for one exception row.
 *
 * matchService.js persists a pairwise "expected vs actual" per exception —
 * never a full PO+GRN+Invoice triple for every line, because a MATCHED line
 * never gets a row at all. Which side "expected" came from depends on the
 * check: quantity checks compare against accepted GRN quantity, price/value/
 * tax checks compare against the PO. This maps each check to the column its
 * `expected_value` actually belongs in, rather than inventing the other two.
 */
const comparisonCells = (exc: MatchException): { po: string; grn: string; invoice: string } => {
  if (QTY_TYPES.includes(exc.exception_type)) {
    return { po: '—', grn: fmtQty(exc.expected_value), invoice: fmtQty(exc.actual_value) };
  }
  if (MONEY_TYPES.includes(exc.exception_type)) {
    return { po: fmtMoney(exc.expected_value), grn: '—', invoice: fmtMoney(exc.actual_value) };
  }
  if (exc.exception_type === 'NO_GRN') {
    const accepted = toNumOrNull(exc.actual_value);
    return { po: '—', grn: accepted === null ? '—' : `${fmtQty(accepted)} accepted`, invoice: '—' };
  }
  if (exc.exception_type === 'VENDOR_MISMATCH') {
    return {
      po: exc.expected_value !== null ? `Vendor #${exc.expected_value}` : '—',
      grn: '—',
      invoice: exc.vendor_name || (exc.actual_value !== null ? `Vendor #${exc.actual_value}` : '—'),
    };
  }
  if (exc.exception_type === 'DUPLICATE_INVOICE') {
    return { po: '—', grn: '—', invoice: exc.actual_value || '—' };
  }
  return { po: '—', grn: '—', invoice: '—' };
};

// ---------------------------------------------------------------------------
// Queue row (client-grouped from the flat exceptions list)
// ---------------------------------------------------------------------------

interface QueueRow {
  invoiceId: number;
  invoiceNumber: string;
  vendorInvoiceNumber: string | null;
  vendorName: string;
  matchStatus: InvoiceMatchStatus;
  blockingOpen: number;
  warningOpen: number;
  hasDuplicate: boolean;
  latestCreatedAt: string;
}

const buildQueue = (rows: MatchException[]): QueueRow[] => {
  const byInvoice = new Map<number, QueueRow>();
  rows.forEach((r) => {
    const existing = byInvoice.get(r.purchase_invoice_id);
    const isOpen = r.status === 'OPEN';
    if (!existing) {
      byInvoice.set(r.purchase_invoice_id, {
        invoiceId: r.purchase_invoice_id,
        invoiceNumber: r.invoice_number,
        vendorInvoiceNumber: r.vendor_invoice_number,
        vendorName: r.vendor_name,
        matchStatus: r.match_status,
        blockingOpen: isOpen && r.severity === 'BLOCKING' ? 1 : 0,
        warningOpen: isOpen && r.severity === 'WARNING' ? 1 : 0,
        hasDuplicate: r.exception_type === 'DUPLICATE_INVOICE',
        latestCreatedAt: r.created_at,
      });
    } else {
      if (isOpen && r.severity === 'BLOCKING') existing.blockingOpen += 1;
      if (isOpen && r.severity === 'WARNING') existing.warningOpen += 1;
      if (r.exception_type === 'DUPLICATE_INVOICE') existing.hasDuplicate = true;
      if (r.created_at > existing.latestCreatedAt) existing.latestCreatedAt = r.created_at;
    }
  });
  return Array.from(byInvoice.values()).sort((a, b) => {
    if (a.blockingOpen !== b.blockingOpen) return b.blockingOpen - a.blockingOpen;
    return b.latestCreatedAt.localeCompare(a.latestCreatedAt);
  });
};

// ---------------------------------------------------------------------------
// Override dialog — deliberately heavy. This is a financial control being
// bypassed, not a toast to dismiss.
// ---------------------------------------------------------------------------

const OverrideDialog: React.FC<{
  exception: MatchException | null;
  onClose: () => void;
  onOverridden: () => void;
}> = ({ exception, onClose, onOverridden }) => {
  const { user } = useAuth();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setReason('');
    setError(null);
  }, [exception]);

  if (!exception) return null;
  const isDuplicate = exception.exception_type === 'DUPLICATE_INVOICE';
  const cells = comparisonCells(exception);

  const handleSubmit = async () => {
    if (!reason.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await scmService.overrideMatchException(exception.id, reason.trim());
      onOverridden();
    } catch (err: any) {
      setError(err.message || 'Failed to override exception');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!exception} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Gavel color="error" />
        Override Match Exception
      </DialogTitle>
      <DialogContent>
        <Alert severity="error" sx={{ mb: 2.5 }}>
          <AlertTitle>This authorizes payment against a mismatch</AlertTitle>
          Overriding clears this exception and marks the invoice <strong>OVERRIDDEN</strong>, which opens the
          payment gate for it despite the finding below. This action is permanent, cannot be undone, and is
          recorded against your name.
        </Alert>

        {isDuplicate && (
          <Alert severity="error" sx={{ mb: 2.5 }}>
            <AlertTitle>Possible duplicate payment</AlertTitle>
            This vendor invoice number was already recorded on another invoice. Overriding this specific check
            means you have personally verified this is <strong>not</strong> a duplicate payment.
          </Alert>
        )}

        <Paper variant="outlined" sx={{ p: 2, mb: 2.5, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <TypePill label={EXCEPTION_LABEL[exception.exception_type]} tint={EXCEPTION_TINT[exception.exception_type]} />
            <StatusDot label={exception.severity} tone={severityTone[exception.severity]} />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {exception.detail || 'No further detail was recorded for this check.'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, fontVariantNumeric: 'tabular-nums' }}>
            {cells.po !== '—' && (
              <Typography variant="caption">
                <strong>PO:</strong> {cells.po}
              </Typography>
            )}
            {cells.grn !== '—' && (
              <Typography variant="caption">
                <strong>GRN:</strong> {cells.grn}
              </Typography>
            )}
            {cells.invoice !== '—' && (
              <Typography variant="caption">
                <strong>Invoice:</strong> {cells.invoice}
              </Typography>
            )}
          </Box>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          required
          multiline
          minRows={3}
          label="Reason for override (required)"
          placeholder="Explain why this payment should proceed despite the mismatch above…"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={submitting}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Overriding as <strong>{user?.name || user?.email || 'you'}</strong> — this reason is stored permanently
          against this exception.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2.5 }}>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleSubmit}
          disabled={submitting || !reason.trim()}
        >
          {submitting ? 'Overriding…' : 'Override & Authorize Payment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Run-match dialog
// ---------------------------------------------------------------------------

const RunMatchDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onEvaluated: (invoiceId: number) => void;
}> = ({ open, onClose, onEvaluated }) => {
  const [invoices, setInvoices] = useState<PurchaseInvoiceOption[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [selected, setSelected] = useState<PurchaseInvoiceOption | null>(null);
  const [evaluating, setEvaluating] = useState(false);
  const [result, setResult] = useState<MatchEvaluateResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSelected(null);
    setResult(null);
    setError(null);
    setLoadingInvoices(true);
    scmService
      .listPurchaseInvoices()
      .then((r) => setInvoices(r.invoices))
      .catch((err: any) => setError(err.message || 'Failed to load invoices'))
      .finally(() => setLoadingInvoices(false));
  }, [open]);

  const handleEvaluate = async () => {
    if (!selected) return;
    setEvaluating(true);
    setError(null);
    try {
      const res = await scmService.evaluateMatch(selected.id);
      setResult(res);
    } catch (err: any) {
      setError(err.message || 'Failed to run three-way match');
    } finally {
      setEvaluating(false);
    }
  };

  const verdictTone: Record<'MATCHED' | 'WITHIN_TOLERANCE' | 'EXCEPTION', StatusTone> = {
    MATCHED: 'success',
    WITHIN_TOLERANCE: 'warning',
    EXCEPTION: 'error',
  };

  return (
    <Dialog open={open} onClose={evaluating ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PlayArrow color="primary" />
        Run Three-Way Match
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Autocomplete
          options={invoices}
          loading={loadingInvoices}
          value={selected}
          onChange={(_, v) => {
            setSelected(v);
            setResult(null);
          }}
          getOptionLabel={(o) => `${o.invoice_no} — ${o.vendor_name}${o.po_number ? ` (${o.po_number})` : ''}`}
          renderInput={(params) => <TextField {...params} label="Purchase Invoice" size="small" sx={{ mt: 1 }} />}
          disabled={evaluating}
        />

        {result && (
          <Box sx={{ mt: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Result:
              </Typography>
              <StatusDot label={result.status} tone={invoiceStatusTone[result.status]} />
            </Box>
            <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, maxHeight: 260, overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>Invoice Line</TableCell>
                    <TableCell sx={headerCellSx}>Verdict</TableCell>
                    <TableCell sx={headerCellSx}>Checks Raised</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.lines.map((line) => (
                    <TableRow key={line.invoiceItemId}>
                      <TableCell>#{line.invoiceItemId}</TableCell>
                      <TableCell>
                        <StatusDot label={line.verdict.replace('_', ' ')} tone={verdictTone[line.verdict]} />
                      </TableCell>
                      <TableCell>
                        {line.reasons.length === 0
                          ? '—'
                          : line.reasons.map((r) => EXCEPTION_LABEL[r.type]).join(', ')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2.5 }}>
        <Button onClick={onClose} disabled={evaluating}>
          {result ? 'Close' : 'Cancel'}
        </Button>
        {result ? (
          <Button
            variant="contained"
            onClick={() => {
              onEvaluated(selected!.id);
              onClose();
            }}
          >
            View in Workbench
          </Button>
        ) : (
          <Button variant="contained" onClick={handleEvaluate} disabled={!selected || evaluating}>
            {evaluating ? 'Evaluating…' : 'Evaluate'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Tolerances dialog
// ---------------------------------------------------------------------------

const TolerancesDialog: React.FC<{ open: boolean; onClose: () => void; onSaved: () => void }> = ({
  open,
  onClose,
  onSaved,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [qtyPct, setQtyPct] = useState('0');
  const [pricePct, setPricePct] = useState('0');
  const [valueAbs, setValueAbs] = useState('0');
  const [allowOver, setAllowOver] = useState(false);
  const [requireGrn, setRequireGrn] = useState(true);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    scmService
      .getTolerances()
      .then(({ tolerances }) => {
        setQtyPct(String(toNumOrNull(tolerances.qty_tolerance_pct) ?? 0));
        setPricePct(String(toNumOrNull(tolerances.price_tolerance_pct) ?? 0));
        setValueAbs(String(toNumOrNull(tolerances.value_tolerance_abs) ?? 0));
        setAllowOver(Number(tolerances.allow_over_receipt) === 1 || tolerances.allow_over_receipt === true);
        setRequireGrn(Number(tolerances.require_grn) === 1 || tolerances.require_grn === true);
      })
      .catch((err: any) => setError(err.message || 'Failed to load tolerances'))
      .finally(() => setLoading(false));
  }, [open]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await scmService.updateTolerances({
        qtyTolerancePct: parseFloat(qtyPct) || 0,
        priceTolerancePct: parseFloat(pricePct) || 0,
        valueToleranceAbs: parseFloat(valueAbs) || 0,
        allowOverReceipt: allowOver,
        requireGrn,
      });
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save tolerances');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Tune color="primary" />
        Match Tolerances
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            <TextField
              label="Quantity Tolerance (%)"
              type="number"
              size="small"
              value={qtyPct}
              onChange={(e) => setQtyPct(e.target.value)}
              helperText="Invoiced qty may exceed accepted GRN qty by up to this percent before it blocks"
            />
            <TextField
              label="Unit Price Tolerance (%)"
              type="number"
              size="small"
              value={pricePct}
              onChange={(e) => setPricePct(e.target.value)}
              helperText="Invoice unit price vs PO unit price"
            />
            <TextField
              label="Line Value Tolerance (absolute)"
              type="number"
              size="small"
              value={valueAbs}
              onChange={(e) => setValueAbs(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              helperText="Absolute rupee drift allowed on qty × price vs the PO-expected line value"
            />
            <Divider />
            <FormControlLabel
              control={<Switch checked={allowOver} onChange={(e) => setAllowOver(e.target.checked)} />}
              label="Allow over-receipt on goods receipts"
            />
            <FormControlLabel
              control={<Switch checked={requireGrn} onChange={(e) => setRequireGrn(e.target.checked)} />}
              label="Require a GRN before an invoice line can match"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2.5 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={loading || saving}>
          {saving ? 'Saving…' : 'Save Tolerances'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const MatchWorkbenchPage: React.FC = () => {
  const [summary, setSummary] = useState<MatchSummary | null>(null);
  const [queueRows, setQueueRows] = useState<MatchException[]>([]);
  const [loadingQueue, setLoadingQueue] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const [statusFilter, setStatusFilter] = useState<ExceptionStatus | ''>('OPEN');
  const [severityFilter, setSeverityFilter] = useState<ExceptionSeverity | ''>('');
  const [typeFilter, setTypeFilter] = useState<ExceptionType | ''>('');

  const [selectedInvoiceId, setSelectedInvoiceId] = useState<number | null>(null);
  /**
   * Collapse the queue to reclaim width for the comparison.
   *
   * The detail pane renders a nine-column PO/GRN/Invoice/variance table. On a
   * 1366 laptop the split left it roughly 640px, so those columns compressed
   * to the point of being unreadable — the reason this screen felt cramped.
   * Collapsing the queue hands the full row back to the comparison.
   */
  const [queueCollapsed, setQueueCollapsed] = useState(false);
  const [detailExceptions, setDetailExceptions] = useState<MatchException[]>([]);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [overrideTarget, setOverrideTarget] = useState<MatchException | null>(null);
  const [runMatchOpen, setRunMatchOpen] = useState(false);
  const [tolerancesOpen, setTolerancesOpen] = useState(false);

  const fetchSummary = useCallback(() => {
    scmService
      .getMatchSummary()
      .then((r) => setSummary(r.summary))
      .catch(() => {
        // Stat row stays hidden — never fabricate these numbers.
      });
  }, []);

  const fetchQueue = useCallback(() => {
    setLoadingQueue(true);
    setError(null);
    scmService
      .getMatchExceptions({
        status: statusFilter || undefined,
        severity: severityFilter || undefined,
        type: typeFilter || undefined,
      })
      .then((r) => setQueueRows(r.exceptions))
      .catch((err: any) => setError(err.message || 'Failed to load match exceptions'))
      .finally(() => setLoadingQueue(false));
  }, [statusFilter, severityFilter, typeFilter]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  const fetchDetail = useCallback((invoiceId: number) => {
    setLoadingDetail(true);
    scmService
      .getMatchExceptions({ invoiceId })
      .then((r) => setDetailExceptions(r.exceptions))
      .catch((err: any) => setToast({ message: err.message || 'Failed to load invoice detail', severity: 'error' }))
      .finally(() => setLoadingDetail(false));
  }, []);

  useEffect(() => {
    if (selectedInvoiceId !== null) fetchDetail(selectedInvoiceId);
  }, [selectedInvoiceId, fetchDetail]);

  // Built from the flat exceptions list fetched with the queue filters. The
  // detail pane below is fetched separately by invoiceId (see fetchDetail),
  // independent of these filters, so a selection survives a re-filter even
  // if the row itself scrolls out of the queue.
  const queue = useMemo(() => buildQueue(queueRows), [queueRows]);

  const refreshAll = () => {
    fetchSummary();
    fetchQueue();
    if (selectedInvoiceId !== null) fetchDetail(selectedInvoiceId);
  };

  const clearFilters = () => {
    setStatusFilter('');
    setSeverityFilter('');
    setTypeFilter('');
  };

  const matchedCount = summary?.byInvoiceMatchStatus.find((r) => r.match_status === 'MATCHED');
  const overriddenCount = summary?.byInvoiceMatchStatus.find((r) => r.match_status === 'OVERRIDDEN');

  const selectedQueueRow = queue.find((q) => q.invoiceId === selectedInvoiceId) || null;
  const headerLevel = detailExceptions.filter((e) => e.invoice_item_id === null);
  const duplicateExceptions = headerLevel.filter((e) => e.exception_type === 'DUPLICATE_INVOICE');
  const otherHeaderLevel = headerLevel.filter((e) => e.exception_type !== 'DUPLICATE_INVOICE');
  const lineGroups = useMemo(() => {
    const map = new Map<number, MatchException[]>();
    detailExceptions
      .filter((e) => e.invoice_item_id !== null)
      .forEach((e) => {
        const key = e.invoice_item_id as number;
        if (!map.has(key)) map.set(key, []);
        map.get(key)!.push(e);
      });
    return Array.from(map.entries()).sort((a, b) => a[0] - b[0]);
  }, [detailExceptions]);

  return (
    <Box>
      <PageHeader
        title="Three-Way Match Workbench"
        subtitle="PO, goods receipt and invoice — reconciled before a payment can go out"
        actions={
          <>
            <Tooltip title="Match tolerances">
              <IconButton onClick={() => setTolerancesOpen(true)}>
                <Tune />
              </IconButton>
            </Tooltip>
            <IconButton onClick={refreshAll} disabled={loadingQueue}>
              <Refresh />
            </IconButton>
            <Button variant="contained" startIcon={<PlayArrow />} size="small" onClick={() => setRunMatchOpen(true)}>
              Run Match
            </Button>
          </>
        }
      />

      {summary && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
            gap: 2,
            mb: 2.5,
          }}
        >
          <StatCard title="Open Blocking" value={summary.openBlocking} icon={<Block />} color="#dc2626" />
          <StatCard title="Open Warning" value={summary.openWarning} icon={<WarningAmber />} color="#d97706" />
          <StatCard
            title="Matched Invoices"
            value={matchedCount ? Number(matchedCount.cnt) : 0}
            icon={<CheckCircle />}
            color="#059669"
          />
          <StatCard
            title="Overridden"
            value={overriddenCount ? Number(overriddenCount.cnt) : 0}
            icon={<Undo />}
            color="#0284c7"
          />
        </Box>
      )}

      <FilterBar>
        <TextField
          select
          size="small"
          label="Exception Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as ExceptionStatus | '')}
          sx={{ minWidth: 170 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FilterList sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        >
          <MenuItem value="">All Statuses</MenuItem>
          <MenuItem value="OPEN">Open</MenuItem>
          <MenuItem value="RESOLVED">Resolved</MenuItem>
          <MenuItem value="OVERRIDDEN">Overridden</MenuItem>
        </TextField>

        <TextField
          select
          size="small"
          label="Severity"
          value={severityFilter}
          onChange={(e) => setSeverityFilter(e.target.value as ExceptionSeverity | '')}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">All Severities</MenuItem>
          <MenuItem value="BLOCKING">Blocking</MenuItem>
          <MenuItem value="WARNING">Warning</MenuItem>
        </TextField>

        <TextField
          select
          size="small"
          label="Type"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as ExceptionType | '')}
          sx={{ minWidth: 190 }}
        >
          <MenuItem value="">All Types</MenuItem>
          {EXCEPTION_TYPES.map((t) => (
            <MenuItem key={t} value={t}>
              {EXCEPTION_LABEL[t]}
            </MenuItem>
          ))}
        </TextField>

        <FilterSpacer />

        {(statusFilter !== '' || severityFilter || typeFilter) && (
          <Button size="small" onClick={clearFilters}>
            Clear Filters
          </Button>
        )}
      </FilterBar>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/*
        Side-by-side from `md` (900px), not `lg` (1200px). MUI breakpoints
        measure the VIEWPORT, but the grid only gets viewport minus the 280px
        sidebar — so at lg a 1366 laptop dropped to a stacked layout while a
        1440 one kept a detail pane too narrow for nine columns. Starting at md
        with a narrower queue keeps the comparison readable on real laptops,
        and collapsing the queue gives it the whole row.
      */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: queueCollapsed ? '1fr' : 'minmax(280px, 340px) 1fr',
          },
          gap: 2.5,
          alignItems: 'flex-start',
        }}
      >
        {/* Queue — hidden entirely when collapsed so the grid reflows to 1fr */}
        <Box sx={{ display: queueCollapsed ? 'none' : 'block', minWidth: 0 }}>
        <TableSurface>
          <TableContainer sx={{ overflowX: 'auto', maxHeight: 640 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>Invoice</TableCell>
                  <TableCell sx={headerCellSx}>Match Status</TableCell>
                  <TableCell sx={headerCellSx} align="right">Open</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingQueue ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 6, border: 0 }}>
                      <CircularProgress size={26} />
                    </TableCell>
                  </TableRow>
                ) : queue.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} sx={{ border: 0 }}>
                      <TableEmptyState title="No invoices match these filters" hint="Run a match, or clear the filters." />
                    </TableCell>
                  </TableRow>
                ) : (
                  queue.map((row) => (
                    <TableRow
                      key={row.invoiceId}
                      hover
                      selected={row.invoiceId === selectedInvoiceId}
                      onClick={() => setSelectedInvoiceId(row.invoiceId)}
                      sx={{ ...bodyRowSx, cursor: 'pointer' }}
                    >
                      <TableCell sx={{ py: 1.25 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {row.invoiceNumber}
                          </Typography>
                          {row.hasDuplicate && (
                            <Tooltip title="Possible duplicate invoice">
                              <ContentCopy sx={{ fontSize: 14, color: 'error.main' }} />
                            </Tooltip>
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {row.vendorName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <StatusDot label={row.matchStatus.replace('_', ' ')} tone={invoiceStatusTone[row.matchStatus]} />
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          {row.blockingOpen > 0 && (
                            <Chip
                              size="small"
                              label={row.blockingOpen}
                              sx={{ bgcolor: 'error.main', color: '#fff', fontWeight: 700, height: 20, minWidth: 26 }}
                            />
                          )}
                          {row.warningOpen > 0 && (
                            <Chip
                              size="small"
                              label={row.warningOpen}
                              sx={{ bgcolor: 'warning.main', color: '#fff', fontWeight: 700, height: 20, minWidth: 26 }}
                            />
                          )}
                          {row.blockingOpen === 0 && row.warningOpen === 0 && (
                            <Typography variant="caption" color="text.secondary">—</Typography>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TableSurface>
        </Box>

        {/* Detail. minWidth:0 is required — a grid child defaults to
            min-width:auto, which lets a wide table push the column wider than
            its track instead of scrolling inside it. That is what made the
            whole page slide sideways. */}
        <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', p: selectedInvoiceId ? 0 : 3, minHeight: 300, minWidth: 0 }}>
          {selectedInvoiceId && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, pt: 1.5, pb: 0 }}>
              <Button
                size="small"
                startIcon={queueCollapsed ? <ChevronRight sx={{ transform: 'rotate(180deg)' }} /> : <ChevronRight />}
                onClick={() => setQueueCollapsed((v) => !v)}
                sx={{ textTransform: 'none', display: { xs: 'none', md: 'inline-flex' } }}
              >
                {queueCollapsed ? 'Show queue' : 'Widen comparison'}
              </Button>
            </Box>
          )}
          {!selectedInvoiceId ? (
            <TableEmptyState
              title="Select an invoice from the queue"
              hint="Its PO, GRN and invoice comparison will appear here, line by line."
            />
          ) : loadingDetail ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ReceiptLong fontSize="small" color="action" />
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {selectedQueueRow?.invoiceNumber || `Invoice #${selectedInvoiceId}`}
                    </Typography>
                    {selectedQueueRow && (
                      <StatusDot
                        label={selectedQueueRow.matchStatus.replace('_', ' ')}
                        tone={invoiceStatusTone[selectedQueueRow.matchStatus]}
                      />
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {selectedQueueRow?.vendorName}
                    {selectedQueueRow?.vendorInvoiceNumber ? ` · Vendor Inv# ${selectedQueueRow.vendorInvoiceNumber}` : ''}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<PlayArrow />}
                  onClick={async () => {
                    try {
                      await scmService.evaluateMatch(selectedInvoiceId);
                      setToast({ message: 'Match re-evaluated', severity: 'success' });
                      refreshAll();
                    } catch (err: any) {
                      setToast({ message: err.message || 'Failed to re-evaluate', severity: 'error' });
                    }
                  }}
                >
                  Re-run Match
                </Button>
              </Box>

              {duplicateExceptions.length > 0 && (
                <Alert
                  severity="error"
                  icon={<ContentCopy />}
                  action={
                    duplicateExceptions[0].status === 'OPEN' ? (
                      <Button color="inherit" size="small" onClick={() => setOverrideTarget(duplicateExceptions[0])}>
                        Override
                      </Button>
                    ) : (
                      <StatusDot label={duplicateExceptions[0].status} tone={exceptionStatusTone[duplicateExceptions[0].status]} />
                    )
                  }
                >
                  <AlertTitle>Possible duplicate invoice — highest-risk finding</AlertTitle>
                  {duplicateExceptions[0].detail}
                </Alert>
              )}

              {otherHeaderLevel.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Header-Level Checks
                  </Typography>
                  <ExceptionMiniTable exceptions={otherHeaderLevel} onOverride={setOverrideTarget} />
                </Box>
              )}

              <Divider />

              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Line-Level Comparison — PO vs GRN vs Invoice
              </Typography>

              {lineGroups.length === 0 ? (
                <Alert severity="success" icon={<CheckCircle />}>
                  No line-level exceptions recorded for this invoice.
                </Alert>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {lineGroups.map(([invoiceItemId, excs]) => (
                    <Box key={invoiceItemId}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                        INVOICE LINE #{invoiceItemId}
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <ExceptionMiniTable exceptions={excs} onOverride={setOverrideTarget} showComparison />
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Paper>
      </Box>

      <OverrideDialog
        exception={overrideTarget}
        onClose={() => setOverrideTarget(null)}
        onOverridden={() => {
          setToast({ message: 'Exception overridden', severity: 'success' });
          setOverrideTarget(null);
          refreshAll();
        }}
      />

      <RunMatchDialog
        open={runMatchOpen}
        onClose={() => setRunMatchOpen(false)}
        onEvaluated={(invoiceId) => {
          setSelectedInvoiceId(invoiceId);
          refreshAll();
        }}
      />

      <TolerancesDialog
        open={tolerancesOpen}
        onClose={() => setTolerancesOpen(false)}
        onSaved={() => setToast({ message: 'Tolerances updated', severity: 'success' })}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// A compact table of exceptions, optionally with the PO/GRN/Invoice
// comparison columns (used for line-level groups, hidden for header-level
// checks like VENDOR_MISMATCH which are shown inline in the alert instead).
// ---------------------------------------------------------------------------

const ExceptionMiniTable: React.FC<{
  exceptions: MatchException[];
  onOverride: (exc: MatchException) => void;
  showComparison?: boolean;
}> = ({ exceptions, onOverride, showComparison }) => (
  <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflowX: 'auto' }}>
    {/* minWidth forces horizontal scroll rather than column squeeze: nine
        columns below ~720px are illegible, not merely tight. */}
    <Table size="small" sx={{ minWidth: showComparison ? 860 : 420 }}>
      <TableHead>
        <TableRow>
          <TableCell sx={headerCellSx}>Check</TableCell>
          <TableCell sx={headerCellSx}>Severity</TableCell>
          {showComparison && (
            <>
              <TableCell sx={headerCellSx} align="right">PO</TableCell>
              <TableCell sx={headerCellSx} align="right">GRN</TableCell>
              <TableCell sx={headerCellSx} align="right">Invoice</TableCell>
              <TableCell sx={headerCellSx} align="right">Variance</TableCell>
              <TableCell sx={headerCellSx} align="right">Var %</TableCell>
            </>
          )}
          <TableCell sx={headerCellSx}>Detail</TableCell>
          <TableCell sx={headerCellSx}>Status</TableCell>
          <TableCell sx={headerCellSx} align="right">Action</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {exceptions.map((exc) => {
          const cells = comparisonCells(exc);
          return (
            <TableRow key={exc.id} sx={bodyRowSx}>
              <TableCell>
                <TypePill label={EXCEPTION_LABEL[exc.exception_type]} tint={EXCEPTION_TINT[exc.exception_type]} />
                {showComparison && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                    {DIMENSION_LABEL[exc.exception_type]}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <StatusDot label={exc.severity} tone={severityTone[exc.severity]} />
              </TableCell>
              {showComparison && (
                <>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{cells.po}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{cells.grn}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{cells.invoice}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {QTY_TYPES.includes(exc.exception_type) ? fmtQty(exc.variance) : fmtMoney(exc.variance)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtPct(exc.variance_pct)}</TableCell>
                </>
              )}
              <TableCell sx={{ maxWidth: 260 }}>
                <Typography variant="caption" color="text.secondary">
                  {exc.detail || '—'}
                </Typography>
              </TableCell>
              <TableCell>
                <StatusDot label={exc.status} tone={exceptionStatusTone[exc.status]} />
              </TableCell>
              <TableCell align="right">
                {exc.status === 'OPEN' ? (
                  <Button size="small" color="error" onClick={() => onOverride(exc)}>
                    Override
                  </Button>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    {exc.resolution_notes ? 'Noted' : '—'}
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </TableContainer>
);

export default MatchWorkbenchPage;
